import { useArrayMap } from '@/shared/lib/hooks/array-map';
import {
  OpensearchIcon,
  RabbitMQIcon,
  CeleryIcon,
  VictoriaMetricsIcon,
} from '@/shared/ui/icons';
import {
  ActionIcon,
  Combobox,
  ComboboxItem,
  Divider,
  Group,
  InputBase,
  Stack,
  ThemeIcon,
  useCombobox,
} from '@mantine/core';
import IconBellCog from '@tabler/icons-react/dist/esm/icons/IconBellCog.mjs';
import IconChevronDown from '@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs';
import IconChevronUp from '@tabler/icons-react/dist/esm/icons/IconChevronUp.mjs';
import IconHome from '@tabler/icons-react/dist/esm/icons/IconHome.mjs';
import IconPointFilled from '@tabler/icons-react/dist/esm/icons/IconPointFilled.mjs';

import { useTranslation } from 'react-i18next';
import {
  ACCESS_RULES_TREE,
  RulesAccess,
  useUserRules,
  checkRules,
} from '@/entities/user/access-rule';

import { useIsAuth } from '@/entities/user/session';

import { filterTree } from '@/shared/lib/utils/tree/filter-tree';

export const ServiceSelector = ({ value, onChange }) => {
  const { t } = useTranslation();
  const isAuth = useIsAuth();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const views = [
    {
      value: 'pamir',
      label: t('main', { ns: 'navigation' }),
      leftSection: <IconHome size={24} />,
    },
    {
      value: 'flower',
      label: 'Flower',
      rule: {
        list: [ACCESS_RULES_TREE.third_party_service.flower.edit],
      },
      leftSection: <CeleryIcon size={24} />,
    },
    {
      value: 'alertmanager',
      label: 'Alertmanager',
      rule: {
        list: [ACCESS_RULES_TREE.third_party_service.alertmanager.edit],
      },
      leftSection: (
        <ThemeIcon variant='transparent' color='#e64a19'>
          <IconBellCog />
        </ThemeIcon>
      ),
    },
    {
      value: 'os_dashboards',
      label: 'Opensearch',
      rule: {
        list: [ACCESS_RULES_TREE.third_party_service.os_dashboards.edit],
      },
      leftSection: <OpensearchIcon size={20} />,
    },
    {
      value: 'rabbitmq',
      label: 'RabbitMQ',
      rule: {
        list: [ACCESS_RULES_TREE.third_party_service.rabbitmq.edit],
      },
      leftSection: <RabbitMQIcon size={24} />,
    },
    {
      group: 'Victoria Metrics',
      leftSection: <VictoriaMetricsIcon size={24} />,
      rule: {
        list: [
          ACCESS_RULES_TREE.third_party_service['metrics-agent'].edit,
          ACCESS_RULES_TREE.third_party_service['metrics-storage'].edit,
          ACCESS_RULES_TREE.third_party_service['metrics-ruler'].edit,
        ],
      },
      items: [
        {
          value: 'metrics-storage',
          label: 'metrics-storage',
          rule: {
            list: [
              ACCESS_RULES_TREE.third_party_service['metrics-storage'].edit,
            ],
          },
        },
        {
          value: 'metrics-agent',
          label: 'metrics-agent',
          rule: {
            list: [ACCESS_RULES_TREE.third_party_service['metrics-agent'].edit],
          },
        },
        {
          value: 'metrics-ruler',
          label: 'metrics-ruler',
          rule: {
            list: [ACCESS_RULES_TREE.third_party_service['metrics-ruler'].edit],
          },
        },
      ],
    },
  ];

  const { data: userRules, isFetched } = useUserRules();

  const viewsMap = useArrayMap(
    views.flatMap((x) =>
      'items' in x && x.items?.length
        ? (x.items?.map((item) => ({
            ...item,
            leftSection: x.leftSection,
          })) as ComboboxItem[])
        : (x as ComboboxItem),
    ),
    'value',
  );

  if (!isAuth || !isFetched) return null;
  return (
    <RulesAccess
      rule={{
        list: [
          ACCESS_RULES_TREE.third_party_service.alertmanager.edit,
          ACCESS_RULES_TREE.third_party_service.flower.edit,
          ACCESS_RULES_TREE.third_party_service.os_dashboards.edit,
          ACCESS_RULES_TREE.third_party_service.rabbitmq.edit,
          ACCESS_RULES_TREE.third_party_service['metrics-agent'].edit,
          ACCESS_RULES_TREE.third_party_service['metrics-storage'].edit,
          ACCESS_RULES_TREE.third_party_service['metrics-ruler'].edit,
        ],
      }}
    >
      <Divider orientation='vertical' my='md' />
      <Combobox
        store={combobox}
        onOptionSubmit={(val) => {
          onChange(val);
          combobox.closeDropdown();
        }}
        width={200}
      >
        <Combobox.Target>
          <InputBase
            component='button'
            type='button'
            pointer
            variant='unstyled'
            rightSection={
              <Stack gap={0}>
                <ThemeIcon variant='transparent' size={12}>
                  <IconChevronUp size={14} stroke={4} />
                </ThemeIcon>
                <Divider />
                <ActionIcon
                  variant='subtle'
                  size={12}
                  color={'var(--mantine-color-blue-6)'}
                >
                  <IconChevronDown size={14} stroke={6} />
                </ActionIcon>
              </Stack>
            }
            rightSectionPointerEvents='none'
            onClick={() => combobox.toggleDropdown()}
          >
            {viewsMap.get(value)?.label}
          </InputBase>
        </Combobox.Target>

        <Combobox.Dropdown>
          <Combobox.Options>
            {filterTree(views, 'items', (x) =>
              x.rule ? checkRules(x.rule, userRules) : true,
            ).map((item) =>
              'group' in item ? (
                <Combobox.Group
                  key={item.group}
                  className='bg-[var(--mantine-color-gray-0)] dark:bg-[var(--mantine-color-dark-5)] rounded-[var(--mantine-radius-sm)]'
                  label={
                    <Group>
                      <ThemeIcon variant='transparent'>
                        {item.leftSection}
                      </ThemeIcon>
                      {item.group}
                    </Group>
                  }
                >
                  {item.items.map((subItem) => (
                    <Combobox.Option key={subItem.value} value={subItem.value}>
                      <Group>
                        <ThemeIcon variant='transparent'>
                          <IconPointFilled size={14} />
                        </ThemeIcon>
                        {subItem.label}
                      </Group>
                    </Combobox.Option>
                  ))}
                </Combobox.Group>
              ) : (
                <Combobox.Option key={item.value} value={item.value}>
                  <Group>
                    <ThemeIcon variant='transparent'>
                      {item.leftSection}
                    </ThemeIcon>
                    {item.label}
                  </Group>
                </Combobox.Option>
              ),
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
    </RulesAccess>
  );
};